import {Entity} from "../domain/entities/entity";
import {PROPERTY_PUBLIC} from "../property-public.symbol";
import {ObjectHelper} from "../helpers/object.helper";


export class ChangeTracker<T extends Entity> {

    private readonly trackedProperties: Array<keyof T> = [];

    private readonly originalValues: Map<keyof T, any> = new Map<keyof T, any>();

    private readonly changedValues: Set<keyof T> = new Set<keyof T>();

    constructor(
        private entity: T
    ) {
        this.trackedProperties = Reflect.getMetadata(PROPERTY_PUBLIC, entity.constructor) as Array<keyof T> || [];
        for (const trackedProperty of this.trackedProperties) {
            this.originalValues.set(trackedProperty, Reflect.get(entity, trackedProperty));
        }
    }

    public get isDirty(): boolean {
        return this.changedValues.size > 0;
    };

    public static create<T extends Entity<T>>(target: T): T & { changeTracker: ChangeTracker<T> } {

        // Cast: isProxy() is true only for a proxy built below, whose get trap answers
        // `changeTracker` — the type system cannot see a property served by a trap.
        if (ObjectHelper.isProxy(target)) {
            return target as T & { changeTracker: ChangeTracker<T> };
        }

        const changeTracker = new ChangeTracker<T>(target);

        // Cast: the get trap below serves `changeTracker`; Proxy<T> is typed as plain T.
        return new Proxy(target, {
            set(target: T, propKey: string | symbol, value: any) {
                if (!Reflect.has(target, propKey)) {
                    throw new Error(`${target.constructor.name} has no property ${String(propKey)}`);
                }

                if (Reflect.get(target, propKey)! === value) {
                    return true;
                }

                // Set the property
                Reflect.set(target, propKey, value);

                // Track the change
                if (changeTracker._isTrackedProperty(propKey)) {
                    changeTracker.trackingValue(propKey, value);
                }

                return true;
            },
            get(target: T, propKey: string | symbol, receiver: any): any {
                if (propKey === 'changeTracker') {
                    return changeTracker;
                }

                if (propKey === '__isProxy') {
                    return true;
                }

                return Reflect.get(target, propKey, receiver);
            }
        }) as T & { changeTracker: ChangeTracker<T> };
    }

    public trackingValue(propKey: keyof T, value: any): void {
        if ((propKey as string).startsWith('_')) {
            return;
        }

        (this.originalValues.has(propKey))
            ? this.changedValues.add(propKey)
            : this.originalValues.set(propKey, value);
    }

    public getOriginalValue(property: keyof T): any {
        return this.originalValues.get(property);
    }

    public getChangedValues(): Partial<T> {
        const obj = Object.fromEntries(Array.from(this.changedValues).map(propKey => {
            return [propKey as PropertyKey, Reflect.get(this.entity, propKey) as any];
        })) as Partial<T>;

        obj.uid = this.entity.uid;

        return obj;
    }

    public getChangedPropertyKeys(): Array<keyof T> {
        return Array.from(this.changedValues);
    }

    public reset(): void {
        this.changedValues.clear();
    }

    /**
     * Whether the given key is one of the tracked (`@Public()`) properties of the entity.
     */
    private _isTrackedProperty(propKey: PropertyKey): propKey is keyof T {
        const trackedProperties: ReadonlyArray<PropertyKey> = this.trackedProperties;

        return trackedProperties.includes(propKey);
    }

}